import React, { useMemo } from 'react';
import WindowedSelect from 'react-windowed-select';
import { FaTrash } from 'react-icons/fa';
import CostCentreSelects, { costCentreLabel } from './CostCentreSelects';
import { COST_CENTRE_LEVELS } from './saleVoucherConstants';
import { clearChildCostCentres, fmt3 } from './saleVoucherHelpers';
import { Section, selectProps } from './formParts';

const cell = { verticalAlign: 'middle' };
const readOnlyCell = { ...cell, textAlign: 'right', background: '#f3f1f7', whiteSpace: 'nowrap' };

// One memoised row: typing in the header or picking the debit account must not re-render every grid select.
const LineRow = React.memo(({ line, index, accountOptions, taxOptions, settings, disabled, mirrored, ccValues, onLine, onRemove }) => (
    <tr>
        <td style={cell}>{index + 1}</td>
        <td style={cell}>
            <WindowedSelect
                {...selectProps} isDisabled={disabled} placeholder="--Select--"
                value={line.acCode ? accountOptions.find((o) => o.value === line.acCode) || { value: line.acCode, label: line.acCode } : null}
                options={accountOptions}
                onChange={(o) => onLine(index, { acCode: o?.value || '', acName: o?.text || '' })}
            />
        </td>
        <td style={cell}>{line.acName}</td>
        <td style={cell}>
            <input
                type="number" step="0.001" className="form-control form-control-sm text-end" disabled={disabled}
                value={line.enteredAmount} onChange={(e) => onLine(index, { enteredAmount: e.target.value })}
            />
        </td>
        <td style={cell}>
            <WindowedSelect
                {...selectProps} isDisabled={disabled}
                value={taxOptions.find((o) => o.value === (line.taxCode || '0')) || null}
                options={taxOptions} onChange={(o) => onLine(index, { taxCode: o?.value || '0' })}
            />
        </td>
        <td style={readOnlyCell}>{fmt3(line.vat)}</td>
        <td style={readOnlyCell}>{fmt3(line.st)}</td>
        <td style={readOnlyCell}>{fmt3(line.total)}</td>
        <td style={readOnlyCell}>{fmt3(line.converted)}</td>
        <td style={cell}>
            <input
                className="form-control form-control-sm" disabled={disabled}
                value={line.narration} onChange={(e) => onLine(index, { narration: e.target.value })}
            />
        </td>
        <CostCentreSelects
            layout="cells" values={ccValues} settings={settings} disabled={disabled || mirrored}
            onChange={(level, value) => {
                const next = clearChildCostCentres(line, level);
                onLine(index, { cc1: next.cc1, cc2: next.cc2, cc3: next.cc3, cc4: next.cc4, [`cc${level}`]: value });
            }}
        />
        {!disabled && (
            <td style={cell}>
                <button type="button" className="btn btn-sm btn-outline-danger" title="Remove row" onClick={() => onRemove(index)}>
                    <FaTrash />
                </button>
            </td>
        )}
    </tr>
));

/** The editable credit lines. Cost-centre cells mirror the header while Copy CC is on. */
const LinesGrid = ({
    form, accounts, taxCodes, settings, disabled, totals, onLine, onAdd, onRemove,
}) => {
    // Memoised so a header change (e.g. picking the debit account) does not rebuild every row's option lists.
    const accountOptions = useMemo(
        () => (accounts || []).map((a) => ({ value: a.value, label: `${a.text} (${a.value})`, text: a.text })), [accounts],
    );
    const taxOptions = useMemo(
        () => (taxCodes || []).map((t) => ({ value: t.taxCode, label: t.taxCode === '0' ? 'No Tax' : `${t.taxCode} - ${t.description}` })), [taxCodes],
    );
    const headerCC = useMemo(() => ({ cc1: form.cc1, cc2: form.cc2, cc3: form.cc3, cc4: form.cc4 }), [form.cc1, form.cc2, form.cc3, form.cc4]);
    const amountHeader = form.taxInclusive ? 'Amount (incl. VAT + ST)' : 'Amount (net)';
    const differs = totals.difference !== 0;

    return (
        <Section title="Credit Lines">
            <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm align-middle mb-2" style={{ minWidth: 1500 }}>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th style={{ minWidth: 260 }}>Account</th>
                            <th style={{ minWidth: 160 }}>Account Name</th>
                            <th style={{ minWidth: 150 }}>{amountHeader}</th>
                            <th style={{ minWidth: 170 }}>Tax Code</th>
                            <th>VAT</th><th>ST</th><th>Total</th><th>Converted</th>
                            <th style={{ minWidth: 180 }}>Narration</th>
                            {COST_CENTRE_LEVELS.map((n) => <th key={n} style={{ minWidth: 150 }}>{costCentreLabel(settings, n)}</th>)}
                            {!disabled && <th />}
                        </tr>
                    </thead>
                    <tbody>
                        {form.lines.map((line, i) => (
                            <LineRow
                                key={line.key} line={line} index={i} accountOptions={accountOptions} taxOptions={taxOptions}
                                settings={settings} disabled={disabled} mirrored={form.copyCC}
                                ccValues={form.copyCC ? headerCC : line} onLine={onLine} onRemove={onRemove}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {!disabled ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onAdd}>+ Add Row</button> : <span />}
                <div className="d-flex gap-4 fw-semibold">
                    <span>Lines: {totals.count}</span>
                    <span>Lines Total: {fmt3(totals.linesTotal)}</span>
                    <span className={differs ? 'text-danger' : 'text-success'}>Difference: {fmt3(totals.difference)}</span>
                </div>
            </div>
        </Section>
    );
};

export default LinesGrid;
